import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import api from '../lib/api';
import { getApiErrorMessage } from '../lib/apiErrors';
import { PersonaAiBadge } from '../components/PersonaAiBadge';
import { isAiGeneratedPersona } from '../lib/personaSource';
import { PERSONA_TYPE_LABELS, personaTypeLabel } from '../lib/personaTypeLabel';
import { practiceModeLabel } from '../lib/practiceModeLabel';
import {
  getFirstBlockingGuidanceSectionId,
  getNewPracticeMandatoryIncompleteCount,
  getScheduleWindowValidationError,
  isNewPracticeSubmittable,
  type GuidanceSectionId,
} from '../lib/newPracticePageGuidance';
import { PracticeTimeLimitControl } from '../components/practice/PracticeTimeLimitControl';
import type { RecordingVisibility } from '../components/practice/types';
import {
  RECORDING_VISIBILITY_OPTIONS,
  SCORES_ALWAYS_VISIBLE_NOTE,
  isRecordingVisibility,
} from '../lib/recordingVisibility';
import {
  NewPracticeBottomBar,
  NewPracticeDesktopBar,
} from '../components/practice/NewPracticeBottomBar';
import { useAuthContext } from '../contexts/AuthContext';
import { useBlockingSectionViewportDirection } from '../hooks/useBlockingSectionViewportDirection';
import { LoadingQuotesDialog } from '../components/LoadingQuotesDialog';

interface Persona {
  id: string;
  name: string;
  jobTitle: string;
  type: string;
  agenda: string;
  source?: string;
}

interface Goal {
  id: string;
  label: string;
  description: string;
  isPlatform?: boolean;
}

const MAX_CUSTOM_GOAL_LABEL_LEN = 200;
const MAX_CUSTOM_GOAL_DESC_LEN = 2000;

interface OrgMember {
  id: string;
  email: string;
  name: string | null;
  role: string;
}

const PITCH_STYLES = [
  { id: 'challenger', label: 'Challenger Sale', description: 'Teach, tailor, take control' },
  { id: 'consultative', label: 'Consultative', description: 'Problem-first, solution-second' },
  { id: 'direct', label: 'Product & Price', description: 'Direct pitch with pricing' },
  {
    id: 'spin',
    label: 'SPIN Selling',
    description: 'Situation, Problem, Implication, Need-payoff',
  },
  { id: 'meddic', label: 'MEDDIC', description: 'Metrics, Economic buyer, Decision criteria...' },
];

export function NewPracticePage() {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [selectedPersonas, setSelectedPersonas] = useState<string[]>([]);
  const [selectedStyle, setSelectedStyle] = useState('');
  const [selectedGoal, setSelectedGoal] = useState('');
  const [customGoalLabel, setCustomGoalLabel] = useState('');
  const [customGoalDescription, setCustomGoalDescription] = useState('');
  const [timeLimitMinutes, setTimeLimitMinutes] = useState(15);
  const timeLimitInitRef = useRef(false);
  const [prefSaving, setPrefSaving] = useState(false);
  const [prefSaved, setPrefSaved] = useState(false);
  const [prefError, setPrefError] = useState<string | null>(null);
  const [mode, setMode] = useState<'verbal' | 'text'>('verbal');
  const [orgMe, setOrgMe] = useState<{
    features: string[];
    maxPracticeTimeLimitMinutes: number;
    preferredPracticeTimeLimitMinutes: number | null;
    myPracticeSessionCount: number;
    practiceUsageReadonly: boolean;
  } | null>(null);
  const [allowOrgTeammates, setAllowOrgTeammates] = useState(false);
  const [recordingVisibility, setRecordingVisibility] = useState<RecordingVisibility | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [sessionTiming, setSessionTiming] = useState<'now' | 'schedule'>('now');
  const [scheduleTitle, setScheduleTitle] = useState('');
  const [scheduleStartLocal, setScheduleStartLocal] = useState('');
  const [scheduleEndLocal, setScheduleEndLocal] = useState('');
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);
  const [inviteUserIds, setInviteUserIds] = useState<string[]>([]);
  const [sendInviteToSelf, setSendInviteToSelf] = useState(false);
  const [personaTypeFilters, setPersonaTypeFilters] = useState<Set<string>>(new Set());
  const [expandedPersonaIds, setExpandedPersonaIds] = useState<Set<string>>(new Set());
  const [goalsTab, setGoalsTab] = useState<'goal' | 'pitchStyle'>('goal');

  useEffect(() => {
    api
      .get('/orgs/me/personas')
      .then((res) => setPersonas(res.data))
      .catch(() => {});
    api
      .get('/practice/goals')
      .then((res) => setGoals(res.data))
      .catch(() => {});
    api
      .get('/orgs/me')
      .then((res) => {
        const data = res.data as {
          features?: string[];
          maxPracticeTimeLimitMinutes?: number;
          preferredPracticeTimeLimitMinutes?: number | null;
          myPracticeSessionCount?: number;
          usage?: { practiceSessions?: { readonly?: boolean } };
          defaultRecordingVisibility?: unknown;
        };
        setRecordingVisibility(
          (prev) =>
            prev ??
            (isRecordingVisibility(data.defaultRecordingVisibility)
              ? data.defaultRecordingVisibility
              : 'rep_only'),
        );
        setOrgMe({
          features: data.features ?? [],
          maxPracticeTimeLimitMinutes: data.maxPracticeTimeLimitMinutes ?? 50,
          preferredPracticeTimeLimitMinutes: data.preferredPracticeTimeLimitMinutes ?? null,
          myPracticeSessionCount: data.myPracticeSessionCount ?? 0,
          practiceUsageReadonly: Boolean(data.usage?.practiceSessions?.readonly),
        });
      })
      .catch(() =>
        setOrgMe({
          features: [],
          maxPracticeTimeLimitMinutes: 50,
          preferredPracticeTimeLimitMinutes: null,
          myPracticeSessionCount: 0,
          practiceUsageReadonly: false,
        }),
      );
  }, []);

  useEffect(() => {
    if (!orgMe || timeLimitInitRef.current) return;
    timeLimitInitRef.current = true;
    const max = orgMe.maxPracticeTimeLimitMinutes;
    let initial = 15;
    if (orgMe.preferredPracticeTimeLimitMinutes != null) {
      initial = Math.min(max, Math.max(1, orgMe.preferredPracticeTimeLimitMinutes));
    }
    setTimeLimitMinutes(initial);
  }, [orgMe]);

  const availableTypes = useMemo(() => {
    const types = new Set(personas.map((p) => p.type));
    return Object.keys(PERSONA_TYPE_LABELS).filter((t) => types.has(t));
  }, [personas]);

  const filteredPersonas = useMemo(() => {
    if (personaTypeFilters.size === 0) return personas;
    return personas.filter((p) => personaTypeFilters.has(p.type));
  }, [personas, personaTypeFilters]);

  const orgFeatures = orgMe?.features ?? null;
  const canMultiPersona = Boolean(orgFeatures?.includes('multi_persona'));
  const canMultiUser = Boolean(orgFeatures?.includes('multi_user'));
  const canPracticeScheduling = Boolean(orgFeatures?.includes('practice_scheduling'));

  useEffect(() => {
    setSendInviteToSelf(sessionTiming === 'schedule');
  }, [sessionTiming]);

  useEffect(() => {
    if (!allowOrgTeammates || !canMultiUser) {
      setOrgMembers([]);
      setInviteUserIds([]);
      return;
    }
    let cancelled = false;
    void api
      .get<OrgMember[]>('/orgs/me/members')
      .then((res) => {
        if (!cancelled) setOrgMembers(res.data);
      })
      .catch(() => {
        if (!cancelled) setOrgMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [allowOrgTeammates, canMultiUser]);

  useEffect(() => {
    if (!allowOrgTeammates) setInviteUserIds([]);
  }, [allowOrgTeammates]);

  const isMultiPersona = selectedPersonas.length > 1;

  const guidanceInput = useMemo(
    () => ({
      orgFeatures,
      selectedPersonas,
      canPracticeScheduling,
      sessionTiming,
      scheduleStartLocal,
      scheduleEndLocal,
    }),
    [
      orgFeatures,
      selectedPersonas,
      canPracticeScheduling,
      sessionTiming,
      scheduleStartLocal,
      scheduleEndLocal,
    ],
  );

  const primaryCtaLabel = isCreating
    ? 'Starting…'
    : sessionTiming === 'schedule' && canPracticeScheduling
      ? 'Create schedule'
      : isMultiPersona
        ? 'Start Committee Practice'
        : 'Start Practice';

  const ctaDisabled =
    isCreating ||
    orgMe === null ||
    orgMe.practiceUsageReadonly ||
    !isNewPracticeSubmittable(guidanceInput);

  const mandatoryIncomplete = getNewPracticeMandatoryIncompleteCount(guidanceInput);

  const outletContext = useOutletContext<{ onOpenMobileMenu?: () => void } | undefined>();
  const onOpenMobileMenu = outletContext?.onOpenMobileMenu ?? (() => {});

  const personaRef = useRef<HTMLDivElement | null>(null);
  const scheduleRef = useRef<HTMLDivElement | null>(null);
  const timeLimitRef = useRef<HTMLDivElement | null>(null);
  const modeRef = useRef<HTMLDetailsElement | null>(null);
  const ctaAnchorRef = useRef<HTMLDivElement | null>(null);
  const createErrorRef = useRef<HTMLParagraphElement | null>(null);

  const scrollBehavior = useCallback((): ScrollBehavior => {
    if (typeof window === 'undefined') return 'auto';
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  }, []);

  const scrollElIntoView = useCallback(
    (el: HTMLElement | null) => {
      if (!el) return;
      el.scrollIntoView({ block: 'start', behavior: scrollBehavior() });
    },
    [scrollBehavior],
  );

  const scrollToGuidanceSection = useCallback(
    (id: GuidanceSectionId) => {
      const el = id === 'persona' ? personaRef.current : scheduleRef.current;
      scrollElIntoView(el);
    },
    [scrollElIntoView],
  );

  const onChipClick = useCallback(
    (section: GuidanceSectionId | 'time' | 'mode') => {
      switch (section) {
        case 'persona':
          scrollElIntoView(personaRef.current);
          break;
        case 'schedule':
          scrollElIntoView(scheduleRef.current);
          break;
        case 'time':
          scrollElIntoView(timeLimitRef.current);
          break;
        case 'mode':
          scrollElIntoView(modeRef.current);
          break;
      }
    },
    [scrollElIntoView],
  );

  const firstBlockingGuidanceSectionId = useMemo((): GuidanceSectionId | null => {
    if (orgMe === null || orgMe.practiceUsageReadonly || guidanceInput.orgFeatures === null) {
      return null;
    }
    if (!ctaDisabled) return null;
    return getFirstBlockingGuidanceSectionId(guidanceInput);
  }, [orgMe, guidanceInput, ctaDisabled]);

  const blockingViewportDirection = useBlockingSectionViewportDirection(
    firstBlockingGuidanceSectionId,
    personaRef,
    scheduleRef,
  );

  const selectedPersonaInfos = useMemo(
    () =>
      selectedPersonas
        .map((id) => {
          const p = personas.find((x) => x.id === id);
          return p ? { name: p.name, type: p.type } : null;
        })
        .filter((p): p is { name: string; type: string } => p !== null),
    [selectedPersonas, personas],
  );

  const scheduleWindowValidationError = useMemo(
    () =>
      getScheduleWindowValidationError(
        canPracticeScheduling,
        sessionTiming,
        scheduleStartLocal,
        scheduleEndLocal,
      ),
    [canPracticeScheduling, sessionTiming, scheduleStartLocal, scheduleEndLocal],
  );

  useEffect(() => {
    if (sessionTiming !== 'schedule' || !canPracticeScheduling) return;
    if (scheduleStartLocal && scheduleEndLocal) return;
    const start = new Date();
    start.setMinutes(start.getMinutes() + 60, 0, 0);
    const end = new Date(start);
    end.setHours(end.getHours() + 1);
    const pad = (n: number) => String(n).padStart(2, '0');
    const toLocal = (d: Date) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    setScheduleStartLocal((prev) => prev || toLocal(start));
    setScheduleEndLocal((prev) => prev || toLocal(end));
  }, [sessionTiming, canPracticeScheduling, scheduleStartLocal, scheduleEndLocal]);

  useEffect(() => {
    if (!createError) return;
    const block = getFirstBlockingGuidanceSectionId(guidanceInput);
    if (block) {
      scrollToGuidanceSection(block);
      return;
    }
    scrollElIntoView(createErrorRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scroll once when error appears
  }, [createError]);

  const togglePersona = (id: string) => {
    setSelectedPersonas((prev) => {
      if (prev.includes(id)) return prev.filter((pid) => pid !== id);
      if (!canMultiPersona) return [id];
      if (prev.length >= 5) return prev;
      return [...prev, id];
    });
  };

  const toggleTypeFilter = (type: string) => {
    setPersonaTypeFilters((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  const togglePersonaExpanded = (id: string) => {
    setExpandedPersonaIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderPersonaCard = (p: Persona) => {
    const isSelected = selectedPersonas.includes(p.id);
    const selIndex = selectedPersonas.indexOf(p.id);
    const isExpanded = expandedPersonaIds.has(p.id);
    const typeLabel = personaTypeLabel(p.type);
    const hasAgenda = Boolean(p.agenda?.trim());

    return (
      <div
        key={p.id}
        data-testid={`new-practice-persona-card-${p.id}`}
        className={`rounded-lg border transition-colors relative ${
          isSelected ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        {isSelected && isMultiPersona && (
          <span className="absolute top-1.5 right-1.5 z-10 w-4 h-4 rounded-full bg-green-700 text-white text-[10px] flex items-center justify-center font-bold">
            {selIndex + 1}
          </span>
        )}
        <button
          type="button"
          onClick={() => togglePersona(p.id)}
          aria-pressed={isSelected}
          className="w-full text-left px-3 py-2.5 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2"
        >
          <div className="flex items-center gap-2 pr-6">
            <div className="font-medium text-gray-900 text-sm truncate">{p.name}</div>
            {isAiGeneratedPersona(p.source) && <PersonaAiBadge />}
          </div>
          <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap items-center gap-1.5">
            <span className="truncate">{p.jobTitle}</span>
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 shrink-0">
              {typeLabel}
            </span>
          </div>
          {hasAgenda && (
            <p
              id={`new-practice-persona-agenda-${p.id}`}
              data-testid={`new-practice-persona-agenda-${p.id}`}
              hidden={!isExpanded}
              className="mt-2 pt-2 border-t border-gray-200/80 text-sm text-gray-600 whitespace-pre-wrap"
            >
              {p.agenda}
            </p>
          )}
        </button>
        {hasAgenda && (
          <div className="px-3 pb-2 -mt-0.5">
            <button
              type="button"
              data-testid={`new-practice-persona-details-toggle-${p.id}`}
              aria-expanded={isExpanded}
              aria-controls={`new-practice-persona-agenda-${p.id}`}
              onClick={() => togglePersonaExpanded(p.id)}
              className="text-xs font-medium text-green-700 hover:text-green-800 focus:outline-none focus-visible:underline"
            >
              {isExpanded ? 'Hide details' : 'View details'}
            </button>
          </div>
        )}
      </div>
    );
  };

  const toggleInviteMember = (memberId: string) => {
    setInviteUserIds((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId],
    );
  };

  const inviteableMembers = useMemo(
    () => orgMembers.filter((m) => m.id !== user?.id),
    [orgMembers, user?.id],
  );

  const sendInvitesIfNeeded = async (
    resource: 'schedule' | 'session',
    resourceId: string,
  ): Promise<{ ok: true } | { ok: false; message: string }> => {
    if (!allowOrgTeammates) return { ok: true };
    if (inviteUserIds.length === 0 && !sendInviteToSelf) return { ok: true };
    const path =
      resource === 'schedule'
        ? `/practice/schedules/${resourceId}/invite-email`
        : `/practice/sessions/${resourceId}/invite-email`;
    try {
      await api.post(path, { userIds: inviteUserIds, includeSelf: sendInviteToSelf });
      return { ok: true };
    } catch (err: unknown) {
      Sentry.captureException(err);
      return { ok: false, message: getApiErrorMessage(err) };
    }
  };

  const handleStart = async () => {
    setCreateError(null);
    if (orgMe?.practiceUsageReadonly) {
      setCreateError('Practice session limit reached for this month.');
      return;
    }
    setIsCreating(true);
    try {
      const usePreset = Boolean(selectedGoal);
      const trimmedCustom = customGoalLabel.trim();
      const useCustom = !usePreset && trimmedCustom.length > 0;
      const base: Record<string, unknown> = {
        personaIds: selectedPersonas,
        pitchStyle: selectedStyle || undefined,
        timeLimitMinutes,
        mode,
        visibility: allowOrgTeammates ? 'org_open' : 'private',
        ...(recordingVisibility ? { recordingVisibility } : {}),
      };
      if (usePreset) {
        base.goalId = selectedGoal;
      } else if (useCustom) {
        base.customGoalLabel = trimmedCustom;
        const d = customGoalDescription.trim();
        if (d) base.customGoalDescription = d;
      }

      if (sessionTiming === 'schedule' && canPracticeScheduling) {
        const scheduleErr = getScheduleWindowValidationError(
          canPracticeScheduling,
          sessionTiming,
          scheduleStartLocal,
          scheduleEndLocal,
        );
        if (scheduleErr) {
          setCreateError(scheduleErr);
          return;
        }
        const start = new Date(scheduleStartLocal);
        const end = new Date(scheduleEndLocal);
        const res = await api.post('/practice/schedules', {
          ...base,
          scheduledStartAt: start.toISOString(),
          scheduledEndAt: end.toISOString(),
          ...(scheduleTitle.trim() ? { title: scheduleTitle.trim() } : {}),
        });
        const scheduleId = res.data.id as string;
        const inviteResult = await sendInvitesIfNeeded('schedule', scheduleId);
        const invitesSentOnSubmit =
          inviteResult.ok && allowOrgTeammates && (inviteUserIds.length > 0 || sendInviteToSelf);
        navigate(`/practice/schedule/${scheduleId}`, {
          state: inviteResult.ok
            ? invitesSentOnSubmit
              ? { justCreatedWithInvitesSent: true }
              : undefined
            : { inviteError: inviteResult.message },
        });
        return;
      }

      const res = await api.post('/practice/sessions', base);
      const sessionId = res.data.id as string;
      const inviteResult = await sendInvitesIfNeeded('session', sessionId);
      const invitesSentOnSubmit =
        inviteResult.ok && allowOrgTeammates && (inviteUserIds.length > 0 || sendInviteToSelf);
      navigate(`/practice/${sessionId}`, {
        state: inviteResult.ok
          ? invitesSentOnSubmit
            ? { justCreatedWithInvitesSent: true }
            : undefined
          : { inviteError: inviteResult.message },
      });
    } catch (err) {
      console.error('Failed to create session', err);
      Sentry.captureException(err);
      setCreateError(getApiErrorMessage(err));
      setIsCreating(false);
    }
  };

  const onFabClick = () => {
    if (!ctaDisabled) {
      void handleStart();
      return;
    }
    if (orgMe?.practiceUsageReadonly) {
      scrollElIntoView(createErrorRef.current);
      return;
    }
    const block = getFirstBlockingGuidanceSectionId(guidanceInput);
    if (block) scrollToGuidanceSection(block);
    else scrollElIntoView(ctaAnchorRef.current);
  };

  return (
    <>
      <div className="pb-8 md:pb-0">
        <div data-tour="np-intro">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">New Practice Session</h1>
          <p className="text-gray-600 mb-8">Choose your persona, time, and goals.</p>
        </div>

        {orgMe?.practiceUsageReadonly && (
          <p className="mb-6 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Your organization has reached its monthly practice session limit. You can finish open
            sessions from the list; new sessions unlock at the start of the next UTC month.
          </p>
        )}

        {createError && (
          <p
            ref={createErrorRef}
            className="mb-6 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
            role="alert"
          >
            {createError}
          </p>
        )}

        {/* Section 1: When */}
        {canPracticeScheduling && (
          <div className="mb-8" id="new-practice-when" data-tour="np-when">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">When</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSessionTiming('now')}
                className={`text-left p-4 rounded-lg border transition-colors ${
                  sessionTiming === 'now'
                    ? 'border-green-600 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium text-gray-900">Start now</div>
                <div className="text-sm text-gray-500">Begin practice immediately</div>
              </button>
              <button
                type="button"
                onClick={() => setSessionTiming('schedule')}
                className={`text-left p-4 rounded-lg border transition-colors ${
                  sessionTiming === 'schedule'
                    ? 'border-green-600 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium text-gray-900">Schedule for later</div>
                <div className="text-sm text-gray-500">
                  Enterprise — pick a time window for your team
                </div>
              </button>
            </div>
            {sessionTiming === 'schedule' && (
              <div
                ref={scheduleRef}
                id="new-practice-schedule"
                className="mt-4 space-y-3 scroll-mt-4"
                data-tour="np-schedule-details"
              >
                <div>
                  <label
                    htmlFor="schedule-title"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Title <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    id="schedule-title"
                    type="text"
                    value={scheduleTitle}
                    onChange={(e) => setScheduleTitle(e.target.value)}
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
                    placeholder="e.g. Q3 enterprise dry run"
                    maxLength={200}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="schedule-start"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Starts
                    </label>
                    <input
                      id="schedule-start"
                      type="datetime-local"
                      value={scheduleStartLocal}
                      onChange={(e) => setScheduleStartLocal(e.target.value)}
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="schedule-end"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Ends
                    </label>
                    <input
                      id="schedule-end"
                      type="datetime-local"
                      value={scheduleEndLocal}
                      onChange={(e) => setScheduleEndLocal(e.target.value)}
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                {scheduleWindowValidationError && selectedPersonas.length > 0 && (
                  <p
                    data-testid="new-practice-nudge-schedule-window"
                    className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                  >
                    {scheduleWindowValidationError} The{' '}
                    <span className="font-medium">{primaryCtaLabel}</span> button will turn green
                    when the schedule is valid.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Section 2: Select Persona */}
        <div
          ref={personaRef}
          id="new-practice-persona"
          className="mb-8 scroll-mt-4"
          data-tour="np-personas"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Select Persona{selectedPersonas.length > 1 ? 's' : ''}{' '}
            {isMultiPersona && (
              <span className="text-sm font-normal text-green-700">
                Buying Committee ({selectedPersonas.length})
              </span>
            )}
            {canMultiPersona && (
              <span className="text-sm font-normal text-gray-400 ml-1">(select up to 5)</span>
            )}
          </h2>
          {orgFeatures !== null && !canMultiPersona && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
              Practice with multiple personas (buying committee) is available on plans that include
              it. You can select one persona per session on your current plan.
            </p>
          )}

          {/* Filter chips */}
          {availableTypes.length > 1 && (
            <div
              className="flex flex-wrap gap-1.5 mb-4"
              role="group"
              aria-label="Filter personas by type"
            >
              {availableTypes.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => toggleTypeFilter(type)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    personaTypeFilters.has(type)
                      ? 'bg-green-100 text-green-800 border border-green-300'
                      : 'bg-gray-100 text-gray-600 border border-transparent hover:bg-gray-200'
                  }`}
                >
                  {personaTypeLabel(type)}
                </button>
              ))}
              {personaTypeFilters.size > 0 && (
                <button
                  type="button"
                  onClick={() => setPersonaTypeFilters(new Set())}
                  className="px-3 py-1.5 rounded-full text-xs font-medium text-gray-500 hover:text-gray-700 border border-transparent hover:bg-gray-100"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}

          {personas.length === 0 ? (
            <p className="text-gray-500">
              No personas available. Ask your admin to enable personas.
            </p>
          ) : filteredPersonas.length === 0 ? (
            <p className="text-gray-500">No personas match the selected filters.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {filteredPersonas.map(renderPersonaCard)}
            </div>
          )}
        </div>

        {/* Section 3: Time limit */}
        {orgMe && (
          <div ref={timeLimitRef} className="mb-8 scroll-mt-4" data-tour="np-time-limit">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Time limit</h2>
            <p className="text-sm text-gray-500 mb-3">
              Every practice session ends automatically at this limit (up to{' '}
              {orgMe.maxPracticeTimeLimitMinutes} minutes for your organization).
            </p>
            <PracticeTimeLimitControl
              idPrefix="new-practice-time"
              maxMinutes={orgMe.maxPracticeTimeLimitMinutes}
              valueMinutes={timeLimitMinutes}
              onChangeMinutes={(m) => {
                setPrefSaved(false);
                setTimeLimitMinutes(m);
              }}
              onSavePreference={async () => {
                setPrefError(null);
                setPrefSaved(false);
                setPrefSaving(true);
                try {
                  await api.patch('/orgs/me/preferences', {
                    preferredPracticeTimeLimitMinutes: timeLimitMinutes,
                  });
                  setPrefSaved(true);
                } catch (err: unknown) {
                  setPrefError(getApiErrorMessage(err));
                  Sentry.captureException(err);
                } finally {
                  setPrefSaving(false);
                }
              }}
              preferenceSaving={prefSaving}
              preferenceSaved={prefSaved}
              preferenceError={prefError}
            />
          </div>
        )}

        {recordingVisibility && (
          <div className="mb-8" data-testid="new-practice-recording-visibility">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
              Who can review this session
            </h2>
            <select
              aria-label="Who can review this session"
              value={recordingVisibility}
              onChange={(e) => setRecordingVisibility(e.target.value as RecordingVisibility)}
              className="w-full sm:w-72 border rounded-md px-3 py-2 text-sm bg-white border-gray-300"
            >
              {RECORDING_VISIBILITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <p className="text-sm text-gray-500 mt-2">
              {RECORDING_VISIBILITY_OPTIONS.find((o) => o.value === recordingVisibility)?.help}
              {recordingVisibility === 'named_viewers'
                ? ' Pick teammates from the session page after you finish.'
                : ''}{' '}
              {SCORES_ALWAYS_VISIBLE_NOTE}
            </p>
          </div>
        )}

        {/* Section 4: Goals (optional) — tabs: Goal Setting | Pitch Style */}
        <div className="mb-8" id="new-practice-goals" data-tour="np-goals">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">
            Goals <span className="text-sm font-normal text-gray-400">(optional)</span>
          </h2>
          <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
            <button
              type="button"
              onClick={() => setGoalsTab('goal')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                goalsTab === 'goal'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Goal Setting
            </button>
            <button
              type="button"
              onClick={() => setGoalsTab('pitchStyle')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                goalsTab === 'pitchStyle'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Pitch Style
            </button>
          </div>

          {goalsTab === 'goal' && (
            <div id="new-practice-goal" data-tour="np-goal">
              {goals.length === 0 ? (
                <p className="text-gray-500">
                  No goals are enabled for your organization yet, or describe your own below.
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {goals.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => {
                        setCustomGoalLabel('');
                        setCustomGoalDescription('');
                        setSelectedGoal(selectedGoal === g.id ? '' : g.id);
                      }}
                      className={`text-left p-4 rounded-lg border transition-colors ${
                        selectedGoal === g.id
                          ? 'border-green-600 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-gray-900">{g.label}</div>
                      {g.description && (
                        <div className="text-sm text-gray-500 mt-1">{g.description}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-4 space-y-2">
                <p className="text-sm text-gray-600">
                  {goals.length === 0 ? 'Describe your own goal' : 'Or describe your own goal'}
                </p>
                <input
                  type="text"
                  value={customGoalLabel}
                  maxLength={MAX_CUSTOM_GOAL_LABEL_LEN}
                  onChange={(e) => {
                    setSelectedGoal('');
                    setCustomGoalLabel(e.target.value);
                  }}
                  placeholder="Custom goal (short label)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <textarea
                  value={customGoalDescription}
                  maxLength={MAX_CUSTOM_GOAL_DESC_LEN}
                  onChange={(e) => {
                    setSelectedGoal('');
                    setCustomGoalDescription(e.target.value);
                  }}
                  placeholder="Optional details for coaching and evaluation"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                />
              </div>
            </div>
          )}

          {goalsTab === 'pitchStyle' && (
            <div id="new-practice-pitch-style" data-tour="np-pitch-style">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {PITCH_STYLES.map((style) => (
                  <button
                    key={style.id}
                    type="button"
                    onClick={() => setSelectedStyle(selectedStyle === style.id ? '' : style.id)}
                    className={`text-left p-4 rounded-lg border transition-colors ${
                      selectedStyle === style.id
                        ? 'border-green-600 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-gray-900">{style.label}</div>
                    <div className="text-sm text-gray-500">{style.description}</div>
                  </button>
                ))}
              </div>
              {!selectedStyle && (
                <p className="mt-3 text-sm text-gray-500">
                  No pitch style selected — defaults to Consultative if left empty.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Section 5: Advanced options (session mode) */}
        <details
          ref={modeRef}
          className="mb-8 group scroll-mt-4"
          id="new-practice-mode"
          data-tour="np-mode"
        >
          <summary
            data-testid="new-practice-advanced-options-toggle"
            className="text-lg font-semibold text-gray-900 cursor-pointer list-none flex items-center justify-between gap-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 [&::-webkit-details-marker]:hidden"
          >
            <span>Advanced options</span>
            <svg
              className="h-5 w-5 shrink-0 text-gray-500 transition-transform group-open:rotate-180"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 7l5 5 5-5" />
            </svg>
          </summary>
          <div
            id="new-practice-advanced-options-panel"
            className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3"
          >
            <button
              type="button"
              data-testid="new-practice-mode-video"
              onClick={() => setMode('verbal')}
              className={`text-left p-4 rounded-lg border transition-colors ${
                mode === 'verbal'
                  ? 'border-green-600 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <svg
                  className="w-5 h-5 text-gray-700"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4M12 15a3 3 0 003-3V5a3 3 0 00-6 0v7a3 3 0 003 3z"
                  />
                </svg>
                <span className="font-medium text-gray-900">{practiceModeLabel('verbal')}</span>
              </div>
              <div className="text-sm text-gray-500">
                Practice out loud in a realistic call simulation
              </div>
            </button>
            <button
              type="button"
              data-testid="new-practice-mode-chat"
              onClick={() => setMode('text')}
              className={`text-left p-4 rounded-lg border transition-colors ${
                mode === 'text'
                  ? 'border-green-600 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <svg
                  className="w-5 h-5 text-gray-700"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                  />
                </svg>
                <span className="font-medium text-gray-900">{practiceModeLabel('text')}</span>
              </div>
              <div className="text-sm text-gray-500">
                Practice by typing your responses in a guided chat
              </div>
            </button>
          </div>
        </details>

        {/* Section 6: Who can join */}
        {canMultiUser && (
          <div className="mb-8" data-tour="np-teammates">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Who can join</h2>
            <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-gray-200 p-4 hover:border-gray-300">
              <input
                type="checkbox"
                className="mt-1 rounded border-gray-300 text-green-700 focus:ring-green-600"
                checked={allowOrgTeammates}
                onChange={(e) => setAllowOrgTeammates(e.target.checked)}
              />
              <span>
                <span className="font-medium text-gray-900">
                  Allow teammates in my organization to join
                </span>
                <span className="block text-sm text-gray-500 mt-1">
                  Anyone signed in with your org can open this session&apos;s link and join as a
                  co-presenter. You can change this later from the session.
                </span>
              </span>
            </label>

            {allowOrgTeammates && (
              <div className="mt-4 rounded-lg border border-gray-200 p-4 space-y-3">
                <p className="text-sm font-medium text-gray-900">Email invites</p>
                <p className="text-sm text-gray-500">
                  Choose teammates to notify by email (with a calendar file). Invites send as soon
                  as you submit. Your account is not listed here; use the checkbox below if you want
                  a copy.
                </p>
                {inviteableMembers.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No other members in this organization yet.
                  </p>
                ) : (
                  <ul className="space-y-2 max-h-48 overflow-y-auto">
                    {inviteableMembers.map((m) => (
                      <li key={m.id}>
                        <label className="flex items-start gap-2 cursor-pointer text-sm">
                          <input
                            type="checkbox"
                            className="mt-0.5 rounded border-gray-300 text-green-700 focus:ring-green-600"
                            checked={inviteUserIds.includes(m.id)}
                            onChange={() => toggleInviteMember(m.id)}
                          />
                          <span>
                            <span className="text-gray-900">{m.name || m.email}</span>
                            {m.name && (
                              <span className="block text-gray-500 text-xs">{m.email}</span>
                            )}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                <label className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5 rounded border-gray-300 text-green-700 focus:ring-green-600"
                    checked={sendInviteToSelf}
                    onChange={(e) => setSendInviteToSelf(e.target.checked)}
                  />
                  <span className="text-gray-800">Send invite to myself</span>
                </label>
              </div>
            )}
          </div>
        )}

        <div ref={ctaAnchorRef} className="scroll-mt-4" />
      </div>

      <NewPracticeDesktopBar
        selectedPersonas={selectedPersonaInfos}
        timeLimitMinutes={timeLimitMinutes}
        mode={mode}
        sessionTiming={sessionTiming}
        ctaDisabled={ctaDisabled}
        ctaLabel={primaryCtaLabel}
        mandatoryIncomplete={mandatoryIncomplete}
        practiceUsageReadonly={orgMe?.practiceUsageReadonly ?? false}
        loading={orgMe === null || mandatoryIncomplete === null}
        onCtaClick={onFabClick}
        onChipClick={onChipClick}
      />

      <NewPracticeBottomBar
        selectedPersonas={selectedPersonaInfos}
        timeLimitMinutes={timeLimitMinutes}
        mode={mode}
        sessionTiming={sessionTiming}
        ctaDisabled={ctaDisabled}
        ctaLabel={primaryCtaLabel}
        mandatoryIncomplete={mandatoryIncomplete}
        practiceUsageReadonly={orgMe?.practiceUsageReadonly ?? false}
        loading={orgMe === null || mandatoryIncomplete === null}
        blockingViewportDirection={blockingViewportDirection}
        onCtaClick={onFabClick}
        onChipClick={onChipClick}
        onOpenMenu={onOpenMobileMenu}
      />

      <LoadingQuotesDialog
        open={isCreating}
        title="Creating your practice session"
        subtitle="Setting things up — this usually takes a few seconds…"
      />
    </>
  );
}
